import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const LEVEL_DOT = {
  OVERDUE: "bg-red-500",
  PENDING: "bg-amber-500",
  IN_PROGRESS: "bg-blue-500",
};

const KEY = "primrose_dismissed_notifs";
const nid = (n) => `${n.type || ""}|${n.title || ""}|${n.detail || ""}`;
const getDismissed = () => { try { return new Set(JSON.parse(localStorage.getItem(KEY) || "[]")); } catch { return new Set(); } };

export default function NotificationBell() {
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [dismissed, setDismissed] = useState(getDismissed());

  useEffect(() => {
    const load = () => api.get("/notifications/feed").then((r) => setFeed(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const visible = feed.filter((n) => !dismissed.has(nid(n)));

  const onClick = (n) => {
    if (n.event_id) navigate(`/events/${n.event_id}`);
  };
  const onDismiss = (e, n) => {
    e.stopPropagation();
    const dd = new Set(dismissed); dd.add(nid(n));
    setDismissed(dd); localStorage.setItem(KEY, JSON.stringify([...dd]));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-beige transition-colors" data-testid="notification-bell" aria-label="Notifications">
          <Bell className="w-5 h-5 text-burgundy" strokeWidth={1.6} />
          {visible.length > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-burgundy text-white text-[9px] font-bold flex items-center justify-center">
              {visible.length > 9 ? "9+" : visible.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b border-gold/20">
          <h4 className="font-semibold text-sm text-burgundy">Notifikasi</h4>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {visible.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[#7A7A7A]">Tidak ada notifikasi.</p>
          ) : (
            visible.map((n, i) => (
              <div key={i} onClick={() => onClick(n)} className="w-full text-left px-4 py-3 border-b border-gold/10 hover:bg-beige/50 transition-colors cursor-pointer flex items-start gap-2.5" data-testid="notification-item">
                <span className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", LEVEL_DOT[n.level] || "bg-gray-400")} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{n.title}</p>
                  <p className="text-xs text-[#7A7A7A] mt-0.5">{n.detail}</p>
                  {n.event_id && <p className="text-[10px] text-burgundy mt-0.5">Klik untuk buka event →</p>}
                </div>
                <button onClick={(e) => onDismiss(e, n)} className="text-[#B0B0B0] hover:text-burgundy shrink-0 p-0.5" data-testid="notification-dismiss" aria-label="Tutup notifikasi"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
